import React from "react";

import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  Chip,
  Box,
  Avatar,
} from "@mui/material";

import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

const projectAccents = [
  { background: "#eff6ff", color: "#2563eb" },
  { background: "#f0fdf4", color: "#16a34a" },
  { background: "#fdf4ff", color: "#a21caf" },
  { background: "#fff7ed", color: "#c2410c" },
  { background: "#ecfeff", color: "#0e7490" },
];

function AssignedProjects({ projects }) {
  return (
    <Card
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.05)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent sx={{ display: "flex", flexDirection: "column", flex: 1, p: 2.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Assigned Projects
          </Typography>

          {projects.length > 0 && (
            <Chip
              size="small"
              label={projects.length}
              sx={{
                fontWeight: 700,
                background: "#eef2ff",
                color: "#4338ca",
              }}
            />
          )}
        </Box>

        {projects.length === 0 && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              py: 4,
              borderRadius: 2,
              border: "1px dashed #dbeafe",
              background: "linear-gradient(145deg, #f8fbff 0%, #eef6ff 100%)",
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                mb: 1.5,
                borderRadius: "50%",
                background: "#dbeafe",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#1d4ed8",
              }}
            >
              <FolderRoundedIcon sx={{ fontSize: 24 }} />
            </Box>

            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1e3a8a" }}>
              No projects assigned
            </Typography>

            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.6,
                mt: 1.2,
                px: 1.2,
                py: 0.5,
                borderRadius: 999,
                background: "#dbeafe",
                color: "#1d4ed8",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              <AutoAwesomeRoundedIcon sx={{ fontSize: 12 }} />
              All caught up
            </Box>
          </Box>
        )}

        {projects.length > 0 && (
          <List
            disablePadding
            sx={{
              flex: 1,
              overflowY: "auto",
              maxHeight: 340,
              display: "flex",
              flexDirection: "column",
              gap: 1.2,
            }}
          >
            {projects.map((project, index) => {
              const accent = projectAccents[index % projectAccents.length];

              return (
                <ListItem
                  key={project.project_id}
                  disableGutters
                  sx={{
                    p: 1.5,
                    borderRadius: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: "#dbeafe",
                      bgcolor: "background.default",
                    },
                  }}
                >
                  <Box display="flex" alignItems="flex-start" gap={1.5} sx={{ width: "100%" }}>
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: 2,
                        background: accent.background,
                        color: accent.color,
                        flexShrink: 0,
                      }}
                    >
                      <FolderRoundedIcon fontSize="small" />
                    </Avatar>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: 14,
                          color: "text.primary",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {project.project_name}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 12.5,
                          color: "text.secondary",
                          mt: 0.3,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {project.description || "No description"}
                      </Typography>

                      <Box display="flex" alignItems="center" gap={1.5} mt={1} flexWrap="wrap">
                        {project.assigned_name && (
                          <Box display="flex" alignItems="center" gap={0.4}>
                            <PersonOutlineOutlinedIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                              {project.assigned_name}
                            </Typography>
                          </Box>
                        )}

                        <Chip
                          size="small"
                          icon={<ConfirmationNumberOutlinedIcon sx={{ fontSize: 14 }} />}
                          label={`${project.ticket_count} ticket${project.ticket_count === 1 ? "" : "s"}`}
                          sx={{
                            borderRadius: 2,
                            background: "#dbeafe",
                            color: "#1d4ed8",
                            fontWeight: 600,
                            fontSize: 11.5,
                            height: 22,
                            "& .MuiChip-icon": { color: "#1d4ed8" },
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );
}

export default AssignedProjects;

